import {
  MIN_VISIBLE_COLS, MIN_VISIBLE_ROWS,
  EXTRA_SCROLL_ROWS, EXTRA_SCROLL_COLS,
  DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT,
  MIN_COL_WIDTH, MIN_ROW_HEIGHT,
} from "../constants";
import { argbToCss, applyTintToHex, resolveExcelColorToCss } from "../utils/colorUtils";
import { richTextToHtml } from "../utils/htmlUtils";
import { qLocal, qLocalAll } from "./xmlUtils";
import { extractChartsFromZip } from "./chartParser";
import { extractPivotTablesFromZip, resolvePivotColors } from "./pivotParser";

/**
 * Extract internal (intra-workbook) hyperlinks from the raw worksheet XML files
 * inside the ZIP.  ExcelJS drops these during its reconciliation phase because
 * internal links use the `location` attribute instead of an `r:id` relationship.
 *
 * Returns a map:  { sheetName → { "r:c" → locationString } }
 * where locationString is the Excel location such as "'Sheet2'!A1" or "Sheet2!$B$5".
 */
async function extractInternalHyperlinksFromZip(zip, sheetNames) {
  const result = {};  // sheetName → { "r:c" → location }
  if (!zip) return result;

  // Build a mapping from worksheet XML path → sheet name.
  // The canonical mapping lives in xl/workbook.xml (sheets → rId) +
  // xl/_rels/workbook.xml.rels (rId → target path).  We resolve it here.
  const sheetPathToName = {};
  try {
    const wbXml = await zip.file("xl/workbook.xml")?.async("string");
    const relsXml = await zip.file("xl/_rels/workbook.xml.rels")?.async("string");
    if (wbXml && relsXml) {
      const wbDoc = new DOMParser().parseFromString(wbXml, "application/xml");
      const relsDoc = new DOMParser().parseFromString(relsXml, "application/xml");

      // Build rId → Target map from relationships
      const rIdMap = {};
      const relNodes = relsDoc.getElementsByTagName("Relationship");
      for (let i = 0; i < relNodes.length; i++) {
        const rel = relNodes[i];
        rIdMap[rel.getAttribute("Id")] = rel.getAttribute("Target");
      }

      // Map each <sheet> element's rId to a worksheet file path
      const sheetNodes = qLocalAll(wbDoc, "sheet");
      sheetNodes.forEach((node) => {
        const name = node.getAttribute("name");
        const rId = node.getAttributeNS(
          "http://schemas.openxmlformats.org/officeDocument/2006/relationships",
          "id"
        ) || node.getAttribute("r:id");
        const target = rIdMap[rId];
        if (name && target) {
          // target is relative like "worksheets/sheet1.xml"
          const fullPath = `xl/${target.replace(/^\//, "")}`;
          sheetPathToName[fullPath] = name;
        }
      });
    }
  } catch (e) {
    console.warn("[extractInternalHyperlinks] Failed to build sheet path map:", e);
  }

  // If the mapping above didn't work, fall back to positional: sheet1.xml → sheetNames[0], etc.
  const worksheetFiles = Object.keys(zip.files).filter(
    (f) => /^xl\/worksheets\/sheet\d+\.xml$/i.test(f)
  ).sort((a, b) => {
    const numA = parseInt(a.match(/sheet(\d+)\.xml/i)?.[1] || "0");
    const numB = parseInt(b.match(/sheet(\d+)\.xml/i)?.[1] || "0");
    return numA - numB;
  });

  for (const filePath of worksheetFiles) {
    const sheetName = sheetPathToName[filePath]
      || sheetNames[worksheetFiles.indexOf(filePath)]
      || null;
    if (!sheetName) continue;

    try {
      const xml = await zip.file(filePath)?.async("string");
      if (!xml) continue;
      const doc = new DOMParser().parseFromString(xml, "application/xml");
      const hyperlinkNodes = qLocalAll(doc, "hyperlink");

      const linksForSheet = {};
      hyperlinkNodes.forEach((node) => {
        const location = node.getAttribute("location");
        const ref = node.getAttribute("ref");
        // Only process internal links (those with a location but no external rId,
        // OR those with both — location takes priority for internal navigation)
        if (location && ref) {
          // ref can be a single cell "A1" or a range "A1:B3"; we use the top-left cell
          const cellRef = ref.includes(":") ? ref.split(":")[0] : ref;
          const m = cellRef.replace(/\$/g, "").match(/^([A-Za-z]+)(\d+)$/);
          if (m) {
            const letters = m[1].toUpperCase();
            let col = 0;
            for (let i = 0; i < letters.length; i++) col = col * 26 + (letters.charCodeAt(i) - 64);
            const r = parseInt(m[2]) - 1;
            const c = col - 1;
            // Prefix with "#" so handleLinkClick recognises it as internal
            linksForSheet[`${r}:${c}`] = location.startsWith("#") ? location : `#${location}`;
          }
        }
      });

      if (Object.keys(linksForSheet).length > 0) {
        result[sheetName] = linksForSheet;
      }
    } catch (e) {
      console.warn(`[extractInternalHyperlinks] Failed to parse ${filePath}:`, e);
    }
  }

  return result;
}

export async function buildWorkbookModelsWithExcelJS(arrayBuffer) {
  const JSZip = (await import("jszip")).default;
  const ExcelJS = (await import("exceljs")).default;

  // Extract theme colors so theme-based fills resolve to actual hex.
  let themeColors = null;
  let zip = null;
  try {
    zip = await JSZip.loadAsync(arrayBuffer);
    const themeXmlFile =
      zip.file("xl/theme/theme1.xml") ||
      zip.file("xl/theme/theme.xml");
    if (themeXmlFile) {
      const themeXml = await themeXmlFile.async("string");
      const doc = new DOMParser().parseFromString(themeXml, "application/xml");
      const scheme = doc.querySelector("clrScheme");
      if (scheme) {
        const order = [
          "lt1", "dk1", "lt2", "dk2",
          "accent1", "accent2", "accent3", "accent4", "accent5", "accent6",
          "hlink", "folHlink",
        ];
        const colors = [];
        order.forEach((key) => {
          const node = scheme.querySelector(key);
          const srgb = node?.querySelector("srgbClr")?.getAttribute("val");
          const sys = node?.querySelector("sysClr")?.getAttribute("lastClr");
          const hex = (srgb || sys || "").trim();
          colors.push(/^[0-9a-fA-F]{6}$/.test(hex) ? hex.toUpperCase() : null);
        });
        themeColors = colors;
      }
    }
  } catch {
    themeColors = null;
  }

  // ── Extract DXF (differential format) records from styles.xml ────────────
  let dxfStyles = [];
  try {
    const stylesXml = await zip?.file("xl/styles.xml")?.async("string");
    if (stylesXml) {
      const stylesDoc = new DOMParser().parseFromString(stylesXml, "application/xml");
      const dxfNodes = qLocalAll(stylesDoc, "dxf");
      dxfStyles = dxfNodes.map(dxf => {
        const result = {};
        // Fill
        const fillNode = qLocal(dxf, "fill");
        const patFill = fillNode ? qLocal(fillNode, "patternFill") : null;
        if (patFill) {
          const fgNode = qLocal(patFill, "fgColor");
          if (fgNode) {
            const argb = fgNode.getAttribute("rgb") || fgNode.getAttribute("argb");
            const theme = fgNode.getAttribute("theme");
            const tint = parseFloat(fgNode.getAttribute("tint") || "0");
            if (argb) {
              result.backgroundColor = argbToCss(argb);
            } else if (theme !== null) {
              const base = Array.isArray(themeColors) ? themeColors[parseInt(theme, 10)] : null;
              if (base) {
                const tinted = applyTintToHex(base, tint);
                result.backgroundColor = `#${tinted}`;
              }
            }
          }
        }
        // Font colour
        const fontNode = qLocal(dxf, "font");
        const colorNode = fontNode ? qLocal(fontNode, "color") : null;
        if (colorNode) {
          const argb = colorNode.getAttribute("rgb") || colorNode.getAttribute("argb");
          const theme = colorNode.getAttribute("theme");
          const tint = parseFloat(colorNode.getAttribute("tint") || "0");
          if (argb) {
            result.color = argbToCss(argb);
          } else if (theme !== null) {
            const base = Array.isArray(themeColors) ? themeColors[parseInt(theme, 10)] : null;
            if (base) result.color = `#${applyTintToHex(base, tint)}`;
          }
        }
        // Bold
        const boldNode = fontNode ? qLocal(fontNode, "b") : null;
        if (boldNode) result.fontWeight = 700;

        return result;
      });
    }
  } catch { /* non-fatal — pivot colors fall back to resolvePivotColors */ }

  const extractedZipCharts = await extractChartsFromZip(zip, themeColors);
  const extractedPivotTables = await extractPivotTablesFromZip(zip);

  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuffer);

  // Collect sheet names first so we can pass them to the hyperlink extractor
  const sheetNames = wb.worksheets.map((s) => s.name || `Sheet${s.id}`);

  // Extract internal hyperlinks that ExcelJS drops during reconciliation
  const internalHyperlinks = await extractInternalHyperlinksFromZip(zip, sheetNames);

  const dataMap = {};

  wb.worksheets.forEach((sheet) => {
    const name = sheet.name || `Sheet${sheet.id}`;

    const maxDataRow = sheet.rowCount || 1;
    const maxDataCol = sheet.columnCount || 1;
    const targetCols = Math.max(maxDataCol + EXTRA_SCROLL_COLS, MIN_VISIBLE_COLS);
    const targetRows = Math.max(maxDataRow + EXTRA_SCROLL_ROWS, MIN_VISIBLE_ROWS);

    const rows = new Array(targetRows).fill(null).map(() => new Array(targetCols).fill(""));
    const formulas = {};
    const styles = {};
    const richHtml = {};
    const links = {};   // key: "r:c" → URL string
    const dataValidations = {}; // key: "r:c" → { type, options: string[] }
    const images = [];
    const charts = extractedZipCharts[sheet.name] || [];

    // Build merge map
    const merges = {};
    const parseColFromLabel = (label) => {
      let col = 0;
      for (let i = 0; i < label.length; i++) col = col * 26 + (label.charCodeAt(i) - 64);
      return col - 1;
    };
    const parseCellRef = (ref) => {
      const m = ref.match(/^([A-Z]+)(\d+)$/i);
      if (!m) return null;
      return { r: parseInt(m[2]) - 1, c: parseColFromLabel(m[1].toUpperCase()) };
    };
    if (sheet.model && Array.isArray(sheet.model.merges)) {
      sheet.model.merges.forEach((mergeRef) => {
        const parts = mergeRef.split(":");
        if (parts.length !== 2) return;
        const tl = parseCellRef(parts[0]);
        const br = parseCellRef(parts[1]);
        if (!tl || !br) return;
        merges[`${tl.r}:${tl.c}`] = { rowSpan: br.r - tl.r + 1, colSpan: br.c - tl.c + 1 };
        for (let mr = tl.r; mr <= br.r; mr++) {
          for (let mc = tl.c; mc <= br.c; mc++) {
            if (mr !== tl.r || mc !== tl.c) merges[`${mr}:${mc}`] = null; // slave
          }
        }
      });
    }

    // Extract images from the worksheet
    try {
      const images1 = sheet.getImages() || [];
      images1.forEach((img) => {
        try {
          const imageId = img.imageId;
          const workbookImage = wb.getImage(imageId);
          if (workbookImage) {
            const base64Image = workbookImage.buffer.toString('base64');
            const mimeType = workbookImage.extension || 'png';
            const dataUrl = `data:image/${mimeType};base64,${base64Image}`;

            // Normalize ExcelJS range format: ExcelJS stores positioning in
            // nativeCol/nativeRow/nativeColOff/nativeRowOff but our rendering
            // components expect col/row/colOff/rowOff. The fractional col/row
            // values from ExcelJS are imprecise — use the native integer values.
            // When native values are absent, convert the fractional part of
            // col/row into an approximate EMU offset so sub-cell positioning
            // is preserved.
            const rawRange = img.range || {};
            const normalizedRange = {};

            const normalizeAnchor = (anchor) => {
              if (!anchor) return undefined;
              if (anchor.nativeCol != null) {
                // Preferred path — high-precision native values
                return {
                  col: anchor.nativeCol,
                  row: anchor.nativeRow != null ? anchor.nativeRow : Math.floor(anchor.row || 0),
                  colOff: anchor.nativeColOff || 0,
                  rowOff: anchor.nativeRowOff || 0,
                };
              }
              // Fallback — derive offset from fractional col/row
              const colVal = anchor.col || 0;
              const rowVal = anchor.row || 0;
              const colInt = Math.floor(colVal);
              const rowInt = Math.floor(rowVal);
              const colFrac = colVal - colInt;
              const rowFrac = rowVal - rowInt;
              // Convert fraction to EMU: fraction × columnWidthPx × 914400/96
              const colWidthPx = colWidths[colInt] || DEFAULT_COL_WIDTH;
              const rowHeightPx = rowHeights[rowInt] || DEFAULT_ROW_HEIGHT;
              return {
                col: colInt,
                row: rowInt,
                colOff: Math.round(colFrac * colWidthPx * (914400 / 96)),
                rowOff: Math.round(rowFrac * rowHeightPx * (914400 / 96)),
              };
            };

            normalizedRange.tl = normalizeAnchor(rawRange.tl);
            normalizedRange.br = normalizeAnchor(rawRange.br);
            if (rawRange.ext) {
              normalizedRange.ext = rawRange.ext;
            }

            images.push({
              id: imageId,
              dataUrl,
              mimeType,
              range: normalizedRange,
              editAs: img.editAs || 'oneCell',
              width: workbookImage.width || 100,
              height: workbookImage.height || 100,
            });
          }
        } catch (imgError) {
          console.warn('[SimpleExcelViewer] Failed to extract individual image:', imgError);
        }
      });
    } catch (imageError) {
      console.warn('[SimpleExcelViewer] Failed to extract images from sheet:', imageError);
    }

    // Column widths: ExcelJS col.width is in Excel "character width" units.
    const colWidths = Array.from({ length: targetCols }, (_, idx) => {
      const col = sheet.getColumn(idx + 1);
      const w = typeof col.width === "number" ? col.width : undefined;
      if (!w) return DEFAULT_COL_WIDTH;
      return Math.max(MIN_COL_WIDTH, Math.round(w * 7 + 5));
    });

    // Row heights: ExcelJS row.height is in POINTS.
    const rowHeights = Array.from({ length: targetRows }, (_, idx) => {
      const row = sheet.getRow(idx + 1);
      const hpt = typeof row.height === "number" ? row.height : undefined;
      if (!hpt) return DEFAULT_ROW_HEIGHT;
      return Math.max(MIN_ROW_HEIGHT, Math.round(hpt * (96 / 72)));
    });

    sheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      if (rowNumber < 1 || rowNumber > targetRows) return;
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        if (colNumber < 1 || colNumber > targetCols) return;

        const r = rowNumber - 1;
        const c = colNumber - 1;

        const v = cell.value;
        // Independent formula extraction
        if (cell.formula) {
          formulas[`${r}:${c}`] = `=${cell.formula.replace(/_xlfn\.|_xludf\./g, "")}`;
        } else if (v && typeof v === "object" && ("formula" in v || "sharedFormula" in v)) {
          const fStr = v.formula || v.sharedFormula || "";
          if (fStr) formulas[`${r}:${c}`] = `=${fStr.replace(/_xlfn\.|_xludf\./g, "")}`;
        }

        // Value resolution
        let hyperlinkUrl = null;
        if (v && typeof v === "object" && "hyperlink" in v) {
          hyperlinkUrl = String(v.hyperlink || "").trim();
          rows[r][c] = typeof v.text === "string" ? v.text : (cell.text ?? "");
        } else if (cell.type === 6 || (v && typeof v === "object" && ("formula" in v || "sharedFormula" in v))) {
          // If it's a formula, we want to show its result
          let resText = "";
          if (v && v.result !== undefined && v.result !== null) {
            if (typeof v.result === "object" && v.result.error) {
              resText = String(v.result.error);
            } else if (typeof v.result === "string") {
              resText = v.result;
            } else {
              // For numbers, dates, booleans: prefer formatted cell.text, fallback to raw result
              if (cell.text !== undefined && cell.text !== null && String(cell.text) !== "[object Object]") {
                resText = String(cell.text);
              } else {
                resText = String(v.result);
              }
            }
          } else if (cell.text !== undefined && cell.text !== null && String(cell.text) !== "[object Object]") {
            resText = String(cell.text);
          }
          rows[r][c] = resText;
        } else if (v && typeof v === "object" && Array.isArray(v.richText)) {
          rows[r][c] = v.richText.map(run => run.text ?? "").join("");
        } else if (typeof v === "string") {
          rows[r][c] = v;
        } else {
          rows[r][c] = cell.text ?? "";
        }

        // Also catch hyperlinks stored via alternative ExcelJS APIs
        if (!hyperlinkUrl && cell.hyperlink) {
          hyperlinkUrl = String(cell.hyperlink).trim();
        }
        if (hyperlinkUrl) links[`${r}:${c}`] = hyperlinkUrl;

        // Rich text (per-run styling)
        if (v && typeof v === "object" && Array.isArray(v.richText)) {
          const html = richTextToHtml(v.richText, themeColors);
          if (html) richHtml[`${r}:${c}`] = html;
        }

        const css = {};
        const font = cell.font || {};
        const fill = cell.fill || {};

        const fontColor = resolveExcelColorToCss(font.color, themeColors);
        if (fontColor) css.color = fontColor;
        if (font.bold) css.fontWeight = 700;
        if (font.italic) css.fontStyle = "italic";
        const textDecorations = [];
        if (font.underline) textDecorations.push("underline");
        if (font.strike) textDecorations.push("line-through");
        if (textDecorations.length > 0) css.textDecoration = textDecorations.join(" ");
        if (typeof font.name === "string") css.fontFamily = `"${font.name}", sans-serif`;
        if (typeof font.size === "number") css.fontSize = `${Math.max(8, Math.round(font.size * 96 / 72))}px`;

        // Fill
        const fillPattern = fill.pattern;
        const fillType = fill.type;
        const isRealFill = (fillType === "pattern" && fillPattern && fillPattern !== "none" && fillPattern !== "gray125")
          || fillType === "gradient";
        if (isRealFill) {
          const fg = resolveExcelColorToCss(fill.fgColor, themeColors);
          const bg = resolveExcelColorToCss(fill.bgColor, themeColors);
          if (fg || bg) css.backgroundColor = fg || bg;
        }

        // Alignment
        const alignment = cell.alignment || {};
        if (alignment.horizontal) {
          const hMap = { left: "left", center: "center", right: "right", fill: "left", justify: "justify", centerContinuous: "center", distributed: "justify" };
          css.textAlign = hMap[alignment.horizontal] || alignment.horizontal;
        }
        if (alignment.vertical) {
          const vMap = { top: "top", middle: "middle", bottom: "bottom", distributed: "middle", justify: "top" };
          css.verticalAlign = vMap[alignment.vertical] || alignment.vertical;
        }
        if (alignment.wrapText) css.whiteSpace = "pre-wrap";

        // Auto-detect line breaks: if the cell value contains newlines,
        // force pre-wrap even when wrapText is not explicitly set, so the
        // viewer displays multi-line content as it appears in Excel.
        if (!alignment.wrapText) {
          const rawStr = typeof v === "string" ? v
            : (v && typeof v === "object" && Array.isArray(v.richText))
              ? v.richText.map(run => run.text ?? "").join("")
              : (cell.text ?? "");
          if (typeof rawStr === "string" && /\r|\n/.test(rawStr)) {
            css.whiteSpace = "pre-wrap";
          }
        }

        // Default right-alignment for numeric/date values — mirrors Excel's built-in behaviour.
        // Only apply when no explicit horizontal alignment is stored in the cell.
        if (!css.textAlign) {
          const isNumeric = typeof v === "number"
            || v instanceof Date
            || (v && typeof v === "object" && ("formula" in v || "sharedFormula" in v) && (typeof v.result === "number" || v.result instanceof Date));
          if (isNumeric) css.textAlign = "right";
        }

        // Borders
        const cellBorder = cell.border || {};
        const bStyleMap = {
          thin: "1px solid", medium: "2px solid", thick: "3px solid",
          dashed: "1px dashed", dotted: "1px dotted", double: "3px double",
          hair: "1px solid", mediumDashed: "2px dashed", dashDot: "1px dashed",
          mediumDashDot: "2px dashed", slantDashDot: "1px dashed",
          dashDotDot: "1px dashed", mediumDashDotDot: "2px dashed",
        };
        ["top", "right", "bottom", "left"].forEach((side) => {
          const b = cellBorder[side];
          if (b && b.style) {
            const bStyle = bStyleMap[b.style] || "1px solid";
            const bColor = resolveExcelColorToCss(b.color, themeColors) || "#000000";
            const prop = `border${side.charAt(0).toUpperCase()}${side.slice(1)}`;
            css[prop] = `${bStyle} ${bColor}`;
          }
        });

        if (Object.keys(css).length > 0) styles[`${r}:${c}`] = css;

        // Data validation (dropdown lists)
        const dv = cell.dataValidation;
        if (dv && dv.type === "list") {
          let options = [];
          if (Array.isArray(dv.formulae) && dv.formulae.length > 0) {
            const formula = dv.formulae[0];
            if (typeof formula === "string") {
              // Direct comma-separated list (e.g. "Option1,Option2,Option3")
              // or could be a cell range reference like "$A$1:$A$5"
              if (!formula.includes("!") && !formula.match(/^\$?[A-Z]+\$?\d+:\$?[A-Z]+\$?\d+$/i)) {
                options = formula.split(",").map(s => s.trim()).filter(Boolean);
              } else {
                // Range reference — resolve values from the sheet
                try {
                  const rangeStr = formula.replace(/^=/, "").replace(/'/g, "");
                  // Handle same-sheet range references like $A$1:$A$5
                  const cleanRange = rangeStr.includes("!") ? rangeStr.split("!")[1] : rangeStr;
                  const match = cleanRange.match(/^\$?([A-Z]+)\$?(\d+):\$?([A-Z]+)\$?(\d+)$/i);
                  if (match) {
                    const startCol = parseColFromLabel(match[1].toUpperCase()) ;
                    const startRow = parseInt(match[2]) - 1;
                    const endCol = parseColFromLabel(match[3].toUpperCase());
                    const endRow = parseInt(match[4]) - 1;
                    for (let vr = startRow; vr <= endRow; vr++) {
                      for (let vc = startCol; vc <= endCol; vc++) {
                        const val = rows[vr]?.[vc];
                        if (val != null && val !== "") options.push(String(val));
                      }
                    }
                  }
                } catch (dvErr) {
                  console.warn("[SimpleExcelViewer] Failed to resolve data validation range:", dvErr);
                }
              }
            }
          }
          if (options.length > 0) {
            dataValidations[`${r}:${c}`] = { type: "list", options };
          }
        }
      });
    });

    // ── Apply pivot table styling ─────────────────────────────────────────────
    const pivotTables = extractedPivotTables[name] || [];
    console.log(`[PivotApply] sheet="${name}" pivotCount=${pivotTables.length}`);
    pivotTables.forEach(pt => {
      const {
        range, styleName,
        firstDataRow,
        firstDataCol,
        showRowHeaders, showColHeaders,
        showRowStripes, showColStripes,
        showLastColumn, showFirstColumn,
        rowGrandTotals, colGrandTotals,
        pivotFormats,
      } = pt;

      const colors = resolvePivotColors(styleName || "PivotStyleLight16", themeColors);
      console.log(`[PivotApply]  "${pt.name}" range=${pt.ref} styleName=${styleName} headerBg=${colors.headerBg} headerColor=${colors.headerColor} stripeBg=${colors.stripeBg} headerRowCount=${Math.max(1, firstDataRow || 1)}`, colors);

      const headerRowCount = Math.max(1, firstDataRow || 1);
      const rowLabelColCount = Math.max(1, firstDataCol || 1);

      // Check whether a cell already has a "real" background from ExcelJS parsing.
      // We treat missing / white / transparent as "empty".
      const isEmptyBg = (bg) =>
        !bg ||
        bg === "#FFFFFF" || bg === "#ffffff" ||
        bg === "white" || bg === "transparent";

      // Determine grand-total row & column positions within the pivot range.
      const grandTotalRow = rowGrandTotals ? range.r2 : -1;
      const grandTotalCol = colGrandTotals ? range.c2 : -1;

      for (let r = range.r1; r <= range.r2; r++) {
        const isHeaderRow = r < range.r1 + headerRowCount;
        const dataRowIndex = r - (range.r1 + headerRowCount);
        const isGrandTotalRow = r === grandTotalRow && rowGrandTotals;

        for (let c = range.c1; c <= range.c2; c++) {
          const key = `${r}:${c}`;
          const existing = styles[key] || {};
          const isRowLabelCol = c < range.c1 + rowLabelColCount;
          const isGrandTotalCol = c === grandTotalCol && colGrandTotals;

          // ── Header row styling ──────────────────────────────────────────────
          if (isHeaderRow) {
            // Pivot header styling is authoritative — always override cell-level
            // styles because pivot formatting takes precedence in Excel.
            if (colors.headerBg) {
              existing.backgroundColor = colors.headerBg;
            }
            if (colors.headerColor) {
              existing.color = colors.headerColor;
            }
            existing.fontWeight = 700;

            // Some sub-variants use a distinct bottom-border on the header instead of a fill
            if (colors.headerBorderBottom) {
              existing.borderBottom = colors.headerBorderBottom;
            }

            // ── Grand total row styling ─────────────────────────────────────────
          } else if (isGrandTotalRow) {
            existing.fontWeight = 700;
            if (colors.grandTotalBg && isEmptyBg(existing.backgroundColor)) {
              existing.backgroundColor = colors.grandTotalBg;
            }
            if (colors.grandTotalColor && !existing.color) {
              existing.color = colors.grandTotalColor;
            }
            if (colors.grandTotalBorderTop) {
              existing.borderTop = colors.grandTotalBorderTop;
            }

            // ── Data row styling ────────────────────────────────────────────────
          } else {
            if ((showRowHeaders || showFirstColumn) && isRowLabelCol) {
              existing.fontWeight = existing.fontWeight || 600;
            }

            // Row stripes — Excel stripes ODD data rows (1, 3, 5…) not even
            if (showRowStripes && isEmptyBg(existing.backgroundColor) && colors.stripeBg) {
              if (dataRowIndex % 2 === 1) {
                existing.backgroundColor = colors.stripeBg;
              }
            }

            // Column stripes — alternating columns within the data area
            if (showColStripes && isEmptyBg(existing.backgroundColor) && colors.stripeBg) {
              const dataColIndex = c - (range.c1 + rowLabelColCount);
              if (dataColIndex >= 0 && dataColIndex % 2 === 1) {
                existing.backgroundColor = colors.stripeBg;
              }
            }
          }

          // ── Grand total column styling ──────────────────────────────────────
          if (isGrandTotalCol && showLastColumn && !isHeaderRow) {
            existing.fontWeight = existing.fontWeight || 700;
            if (colors.grandTotalBg && isEmptyBg(existing.backgroundColor) && !isGrandTotalRow) {
              existing.backgroundColor = colors.grandTotalBg;
            }
          }

          // ── Borders ─────────────────────────────────────────────────────────
          if (colors.borderColor) {
            if (!existing.borderBottom) existing.borderBottom = `1px solid ${colors.borderColor}`;
            if (!existing.borderRight) existing.borderRight = `1px solid ${colors.borderColor}`;
            // Also apply left border on the first column and top border on the first data row
            if (c === range.c1 && !existing.borderLeft) {
              existing.borderLeft = `1px solid ${colors.borderColor}`;
            }
            if (r === range.r1 && !existing.borderTop) {
              existing.borderTop = `1px solid ${colors.borderColor}`;
            }
          }

          styles[key] = existing;
        }
      }

      // ── DXF overrides from pivot's <formats> section ────────────────────────
      if (Array.isArray(pt.pivotFormats) && dxfStyles.length > 0) {
        pt.pivotFormats.forEach(({ dxfId }) => {
          const dxf = dxfStyles[dxfId];
          if (!dxf) return;
          const hCount = Math.max(1, firstDataRow || 1);
          for (let r = range.r1; r < range.r1 + hCount; r++) {
            for (let c = range.c1; c <= range.c2; c++) {
              const key = `${r}:${c}`;
              const s = styles[key] || {};
              if (dxf.backgroundColor) s.backgroundColor = dxf.backgroundColor;
              if (dxf.color) s.color = dxf.color;
              if (dxf.fontWeight) s.fontWeight = dxf.fontWeight;
              styles[key] = s;
            }
          }
        });
      }
    });

    // ── Border propagation ────────────────────────────────────────────────────
    const getBorderPx = (cssStr) => {
      if (!cssStr) return 0;
      const m = cssStr.match(/^(\d+)px/);
      return m ? parseInt(m[1]) : 1;
    };

    const borderCellKeys = Object.keys(styles).filter((k) => {
      const s = styles[k];
      return s.borderTop || s.borderRight || s.borderBottom || s.borderLeft;
    });

    borderCellKeys.forEach((key) => {
      const [r, c] = key.split(":").map(Number);
      const s = styles[key];

      const propagate = (fromProp, adjR, adjC, toProp) => {
        const val = s[fromProp];
        if (!val) return;
        if (adjR < 0 || adjC < 0 || adjR >= targetRows || adjC >= targetCols) return;
        const adjKey = `${adjR}:${adjC}`;
        const adj = styles[adjKey] || {};
        if (getBorderPx(val) >= getBorderPx(adj[toProp])) {
          adj[toProp] = val;
          styles[adjKey] = adj;
        }
      };

      propagate("borderTop", r - 1, c, "borderBottom");
      propagate("borderLeft", r, c - 1, "borderRight");
      propagate("borderBottom", r + 1, c, "borderTop");
      propagate("borderRight", r, c + 1, "borderLeft");
    });

    // Merge internal hyperlinks extracted from the raw XML.
    // Only fill in entries that ExcelJS didn't already resolve (external links).
    const internalLinksForSheet = internalHyperlinks[name] || {};
    Object.keys(internalLinksForSheet).forEach((key) => {
      if (!links[key]) {
        links[key] = internalLinksForSheet[key];
      }
    });

    // Freeze panes — ExcelJS stores in sheet.views[0]
    let frozenRows = 0;
    let frozenCols = 0;
    const sheetViews = sheet.views || [];
    if (sheetViews.length > 0) {
      const view = sheetViews[0];
      if (view.state === "frozen") {
        frozenRows = typeof view.ySplit === "number" ? view.ySplit : 0;
        frozenCols = typeof view.xSplit === "number" ? view.xSplit : 0;
      }
    }

    dataMap[name] = { rows, maxCols: targetCols, colWidths, rowHeights, formulas, styles, richHtml, links, images, charts, pivotTables, merges, dataValidations, frozenRows, frozenCols };
  });

  return { sheetNames, dataMap };
}
